import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  X,
  Loader2,
  Building2,
  RefreshCw
} from 'lucide-react';
import api, { Transaction, FinanceStats, Client, Project, MercuryTransaction, MercuryAccount } from '../services/api';

type DataSource = 'mercury' | 'local' | 'pnl';
type PnLPeriod = 'all' | 'month' | 'quarter' | 'year';

const FinancePage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [mercuryTransactions, setMercuryTransactions] = useState<MercuryTransaction[]>([]);
  const [mercuryAccounts, setMercuryAccounts] = useState<MercuryAccount[]>([]);
  const [stats, setStats] = useState<FinanceStats | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [mercuryLoading, setMercuryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [dataSource, setDataSource] = useState<DataSource>('mercury');
  const [pnlPeriod, setPnlPeriod] = useState<PnLPeriod>('month');
  const [showInternalTxns, setShowInternalTxns] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');

  // Get unique categories from transactions
  const uniqueCategories = Array.from(new Set(mercuryTransactions.map(t => t.category).filter(Boolean))).sort();
  const [formData, setFormData] = useState({
    transaction_type: 'income' as 'income' | 'expense',
    category: 'other',
    amount: '',
    description: '',
    client: '',
    project: '',
    status: 'completed' as 'completed' | 'pending' | 'failed',
    transaction_date: new Date().toISOString().split('T')[0]
  });

  const fetchLocalData = useCallback(async () => {
    try {
      const [transactionsRes, clientsRes, projectsRes] = await Promise.all([
        api.getTransactions({
          transaction_type: selectedType !== 'all' ? selectedType : undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          search: searchQuery || undefined
        }),
        api.getClients(),
        api.getProjects()
      ]);
      setTransactions(transactionsRes.results);
      setClients(clientsRes.results);
      setProjects(projectsRes.results);
    } catch (err) {
      console.error('Failed to load local transactions:', err);
    }
  }, [selectedType, selectedStatus, searchQuery]);

  const fetchMercuryData = useCallback(async () => {
    try {
      setMercuryLoading(true);
      const [statsRes, accountsRes, transactionsRes] = await Promise.all([
        api.getFinanceStats(30),
        api.getMercuryAccounts(),
        api.getMercuryTransactions()
      ]);
      setStats(statsRes);
      setMercuryAccounts(accountsRes.accounts || []);
      setMercuryTransactions(transactionsRes.results || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load Mercury data:', err);
      setError('Failed to connect to Mercury. Showing local data.');
      setDataSource('local');
    } finally {
      setMercuryLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchLocalData(), fetchMercuryData()]);
    setLoading(false);
  }, [fetchLocalData, fetchMercuryData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefreshMercury = async () => {
    await fetchMercuryData();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        client: formData.client || null,
        project: formData.project || null
      };

      if (editingTransaction) {
        await api.updateTransaction(editingTransaction.id, data);
      } else {
        await api.createTransaction(data);
      }
      setShowModal(false);
      setEditingTransaction(null);
      resetForm();
      fetchLocalData();
    } catch (err) {
      console.error(err);
      alert('Failed to save transaction');
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      transaction_type: transaction.transaction_type,
      category: transaction.category,
      amount: transaction.amount,
      description: transaction.description,
      client: transaction.client || '',
      project: transaction.project || '',
      status: transaction.status,
      transaction_date: transaction.transaction_date
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await api.deleteTransaction(id);
        fetchLocalData();
      } catch (err) {
        console.error(err);
        alert('Failed to delete transaction');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'income',
      category: 'other',
      amount: '',
      description: '',
      client: '',
      project: '',
      status: 'completed',
      transaction_date: new Date().toISOString().split('T')[0]
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return '';
    }
  };

  const getCategoryDisplay = (category: string) => {
    const categoryMap: Record<string, string> = {
      // Income
      'project_payment': 'Client Payment',
      'retainer': 'Retainer Fee',
      'consultation': 'Consultation',
      'transfer': 'Transfer',
      'cashback': 'Cashback',
      'internal': 'Internal',
      'refund': 'Refund',
      // Expenses
      'ai_tools': 'AI & Dev Tools',
      'hosting': 'Hosting',
      'domains': 'Domains & DNS',
      'software': 'Software/SaaS',
      'marketing': 'Marketing',
      'communication': 'Communication',
      'equipment': 'Equipment',
      'advertising': 'Advertising',
      'fees': 'Fees',
      'salary': 'Salary',
      'office': 'Office',
      'other': 'Other'
    };
    return categoryMap[category] || category;
  };

  // P&L Calculations - with transfer detection
  const isTransferTransaction = (txn: MercuryTransaction) => {
    const desc = (txn.description || '').toLowerCase();
    const counterparty = (txn.counterparty_name || '').toLowerCase();
    const category = (txn.category || '').toLowerCase();

    // Check for common transfer patterns
    const transferKeywords = [
      'transfer', 'credit card', 'card payment', 'payment to card',
      'internal transfer', 'move money', 'account transfer',
      'pay credit', 'cc payment', 'card payoff',
      'io autopay', 'autopay',  // Mercury IO credit card autopay
      // Cashback removed - should appear as income
    ];

    // Exclude Stripe transfers - those are real income
    if (counterparty.includes('stripe')) return false;

    return transferKeywords.some(keyword =>
      desc.includes(keyword) || counterparty.includes(keyword)
    ) || category === 'transfer' || category === 'credit_card_payment';
  };

  const getFilteredTransactionsForPnL = () => {
    const txns = mercuryTransactions;
    const now = new Date();

    // First filter by period
    const periodFiltered = txns.filter(txn => {
      if (!txn.transaction_date) return false;
      const txnDate = new Date(txn.transaction_date);

      switch (pnlPeriod) {
        case 'month':
          return txnDate.getMonth() === now.getMonth() && txnDate.getFullYear() === now.getFullYear();
        case 'quarter':
          const currentQuarter = Math.floor(now.getMonth() / 3);
          const txnQuarter = Math.floor(txnDate.getMonth() / 3);
          return txnQuarter === currentQuarter && txnDate.getFullYear() === now.getFullYear();
        case 'year':
          return txnDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    // Separate transfers from real transactions
    const transfers = periodFiltered.filter(isTransferTransaction);
    const realTransactions = periodFiltered.filter(txn => !isTransferTransaction(txn));

    // Auto-detect matching pairs (same amount, opposite types within 3 days)
    const matchedTransferIds = new Set<string>();
    realTransactions.forEach(txn1 => {
      if (matchedTransferIds.has(txn1.id)) return;
      const amount1 = Math.abs(parseFloat(txn1.amount) || 0);
      const date1 = new Date(txn1.transaction_date);

      realTransactions.forEach(txn2 => {
        if (txn1.id === txn2.id || matchedTransferIds.has(txn2.id)) return;
        const amount2 = Math.abs(parseFloat(txn2.amount) || 0);
        const date2 = new Date(txn2.transaction_date);

        // Same amount, opposite types, within 3 days
        if (
          Math.abs(amount1 - amount2) < 0.01 &&
          txn1.transaction_type !== txn2.transaction_type &&
          Math.abs(date1.getTime() - date2.getTime()) < 3 * 24 * 60 * 60 * 1000
        ) {
          matchedTransferIds.add(txn1.id);
          matchedTransferIds.add(txn2.id);
        }
      });
    });

    return {
      realTransactions: realTransactions.filter(txn => !matchedTransferIds.has(txn.id)),
      transfers: [...transfers, ...realTransactions.filter(txn => matchedTransferIds.has(txn.id))],
      allTransactions: periodFiltered
    };
  };

  const calculatePnL = () => {
    const { realTransactions, transfers } = getFilteredTransactionsForPnL();

    const incomeByCategory: Record<string, number> = {};
    const expensesByCategory: Record<string, number> = {};
    let totalIncome = 0;
    let totalExpenses = 0;
    let totalTransfers = 0;

    realTransactions.forEach(txn => {
      const amount = parseFloat(txn.amount) || 0;
      const category = txn.category || 'other';

      if (txn.transaction_type === 'income') {
        incomeByCategory[category] = (incomeByCategory[category] || 0) + amount;
        totalIncome += amount;
      } else {
        expensesByCategory[category] = (expensesByCategory[category] || 0) + amount;
        totalExpenses += amount;
      }
    });

    transfers.forEach(txn => {
      const amount = parseFloat(txn.amount) || 0;
      totalTransfers += txn.transaction_type === 'income' ? amount : -amount;
    });

    return {
      incomeByCategory,
      expensesByCategory,
      totalIncome,
      totalExpenses,
      netProfit: totalIncome - totalExpenses,
      transactionCount: realTransactions.length,
      transferCount: transfers.length,
      totalTransfers
    };
  };

  const pnlData = calculatePnL();

  const getPeriodLabel = () => {
    const now = new Date();
    switch (pnlPeriod) {
      case 'month':
        return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `Q${quarter} ${now.getFullYear()}`;
      case 'year':
        return now.getFullYear().toString();
      default:
        return 'All Time';
    }
  };

  // Filter Mercury transactions based on search and filters
  const filteredMercuryTransactions = mercuryTransactions.filter(txn => {
    // Exclude internal transfers unless toggle is on
    if (!showInternalTxns && isTransferTransaction(txn)) return false;

    const matchesSearch = !searchQuery ||
      txn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      txn.counterparty_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || txn.transaction_type === selectedType;
    const matchesStatus = selectedStatus === 'all' || txn.status === selectedStatus;
    const matchesMonth = selectedMonth === 'all' || (txn.transaction_date && txn.transaction_date.startsWith(selectedMonth));
    const matchesCategory = selectedCategory === 'all' || txn.category === selectedCategory;
    const matchesYear = selectedYear === 'all' || (txn.transaction_date && txn.transaction_date.startsWith(selectedYear));
    return matchesSearch && matchesType && matchesStatus && matchesMonth && matchesCategory && matchesYear;
  }).sort((a, b) => {
    const dateA = new Date(a.transaction_date || 0).getTime();
    const dateB = new Date(b.transaction_date || 0).getTime();
    return dateB - dateA; // Sort descending (newest first)
  });

  if (loading && transactions.length === 0 && mercuryTransactions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Finance</h1>
              <p className="text-gray-600 dark:text-gray-300">
                {dataSource === 'mercury' ? 'Connected to Mercury Banking' : 'Local transactions'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {dataSource === 'mercury' && (
                <button
                  onClick={handleRefreshMercury}
                  disabled={mercuryLoading}
                  className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${mercuryLoading ? 'animate-spin' : ''}`} />
                  Sync
                </button>
              )}
              <button className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                <FileText className="w-5 h-5" />
                Export
              </button>
              <button
                onClick={() => { resetForm(); setEditingTransaction(null); setShowModal(true); }}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Transaction
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Mercury Accounts */}
        {dataSource === 'mercury' && mercuryAccounts.length > 0 && (
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Mercury Accounts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mercuryAccounts.map((account) => (
                <div key={account.id} className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{account.name || 'Account'}</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    ${(account.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Available: ${(account.available_balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
            {stats.total_balance !== undefined && (
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  ${stats.total_balance.toLocaleString()}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Total Balance</p>
              </div>
            )}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-primary-500" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  <ArrowUpRight className="w-4 h-4 inline ml-1" />
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                ${stats.total_revenue.toLocaleString()}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">Total Revenue</p>
              {stats.period_start && (
                <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
              )}
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400">
                  <ArrowDownRight className="w-4 h-4 inline ml-1" />
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                ${stats.total_expenses.toLocaleString()}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">Expenses</p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  <ArrowUpRight className="w-4 h-4 inline ml-1" />
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                ${stats.net_profit.toLocaleString()}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">Net Profit</p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-yellow-500" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                ${stats.pending_payments.toLocaleString()}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">Pending</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-2">
            {error}
          </div>
        )}

        {/* Data Source Tabs */}
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => setDataSource('mercury')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              dataSource === 'mercury'
                ? 'bg-primary-500 text-black'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
            }`}
          >
            Transactions
          </button>
          <button
            onClick={() => setDataSource('pnl')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              dataSource === 'pnl'
                ? 'bg-primary-500 text-black'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
            }`}
          >
            P&L Report
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-2">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search transactions..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-4">
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="all">All Months</option>
                <option value="2026-01">January 2026</option>
                <option value="2025-12">December 2025</option>
                <option value="2025-11">November 2025</option>
                <option value="2025-10">October 2025</option>
                <option value="2025-09">September 2025</option>
                <option value="2025-08">August 2025</option>
                <option value="2025-07">July 2025</option>
                <option value="2025-06">June 2025</option>
                <option value="2025-05">May 2025</option>
                <option value="2025-04">April 2025</option>
                <option value="2025-03">March 2025</option>
                <option value="2025-02">February 2025</option>
                <option value="2025-01">January 2025</option>
              </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInternalTxns}
                  onChange={(e) => setShowInternalTxns(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500"
                />
                Show internal
              </label>
            </div>
          </div>
        </div>

        {/* P&L Report View */}
        {dataSource === 'pnl' && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                Profit & Loss - {getPeriodLabel()}
              </h2>
              <div className="flex gap-2">
                {(['month', 'quarter', 'year', 'all'] as PnLPeriod[]).map((period) => (
                  <button
                    key={period}
                    onClick={() => setPnlPeriod(period)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      pnlPeriod === period
                        ? 'bg-primary-500 text-black'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {period === 'all' ? 'All Time' : period.charAt(0).toUpperCase() + period.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Income</span>
                </div>
                <p className="text-base font-bold text-green-600 dark:text-green-400">
                  ${pnlData.totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Expenses</span>
                </div>
                <p className="text-base font-bold text-red-600 dark:text-red-400">
                  ${pnlData.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-primary-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Net Profit</span>
                </div>
                <p className={`text-base font-bold ${pnlData.netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {pnlData.netProfit >= 0 ? '+' : '-'}${Math.abs(pnlData.netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Income by Category */}
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                    Income by Category
                  </h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {Object.entries(pnlData.incomeByCategory).length === 0 ? (
                    <div className="px-6 py-4 text-gray-500 dark:text-gray-400 text-sm">No income recorded</div>
                  ) : (
                    Object.entries(pnlData.incomeByCategory)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, amount]) => (
                        <div key={category} className="px-6 py-3 flex justify-between items-center">
                          <span className="text-gray-700 dark:text-gray-300">{getCategoryDisplay(category)}</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            +${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* Expenses by Category */}
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                    Expenses by Category
                  </h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {Object.entries(pnlData.expensesByCategory).length === 0 ? (
                    <div className="px-6 py-4 text-gray-500 dark:text-gray-400 text-sm">No expenses recorded</div>
                  ) : (
                    Object.entries(pnlData.expensesByCategory)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, amount]) => (
                        <div key={category} className="px-6 py-3 flex justify-between items-center">
                          <span className="text-gray-700 dark:text-gray-300">{getCategoryDisplay(category)}</span>
                          <span className="font-medium text-red-600 dark:text-red-400">
                            -${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Transaction Count & Transfers Info */}
            <div className="text-center space-y-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Based on {pnlData.transactionCount} transaction{pnlData.transactionCount !== 1 ? 's' : ''} in this period
              </p>
              {pnlData.transferCount > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {pnlData.transferCount} internal transfer{pnlData.transferCount !== 1 ? 's' : ''} excluded (${pnlData.totalTransfers.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                </p>
              )}
            </div>
          </div>
        )}

        {/* Transactions Table */}
        {dataSource === 'mercury' && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {filteredMercuryTransactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  {mercuryLoading ? 'Loading transactions...' : 'No transactions found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Transaction</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Category</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Amount</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Status</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Account</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMercuryTransactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-4 px-6">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{transaction.description}</p>
                            {transaction.counterparty_name && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">{transaction.counterparty_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                            {getCategoryDisplay(transaction.category)}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className={transaction.transaction_type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {transaction.transaction_type === 'income' ? '+' : '-'}${parseFloat(transaction.amount).toLocaleString()}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(transaction.status)}`}>
                            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {transaction.account_name}
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400">
                          {transaction.transaction_date ? new Date(transaction.transaction_date).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                {editingTransaction ? 'Edit Transaction' : 'New Transaction'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value as 'income' | 'expense' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="project_payment">Project Payment</option>
                    <option value="retainer">Retainer Fee</option>
                    <option value="consultation">Consultation</option>
                    <option value="software">Software/Tools</option>
                    <option value="marketing">Marketing Spend</option>
                    <option value="salary">Salary</option>
                    <option value="office">Office Expenses</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client (Optional)</label>
                  <select
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">No client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project (Optional)</label>
                  <select
                    value={formData.project}
                    onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">No project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'completed' | 'pending' | 'failed' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                  <input
                    type="date"
                    required
                    value={formData.transaction_date}
                    onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600"
                >
                  {editingTransaction ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancePage;
